import { useNavigate } from '@tanstack/react-router';
import { Children, isValidElement, Key, ReactElement, ReactNode } from 'react';
import { SelectionIndicator, Tab, TabList, TabPanel, Tabs } from 'react-aria-components';
import { useSearchString } from 'src/routing/routeSearch';

import { cn } from './utils';

type AgTabProps = {
  id: string;
  label: string;
  icon?: ReactElement;
  iconAfter?: ReactElement;
  indicatorValue?: ReactNode;
  children?: ReactNode;
  'data-testid'?: string;
};

type AgTabsProps = {
  children: ReactNode;
  defaultTab?: string;
  aboveTabs?: ReactNode;
  onTabChange?: (key: Key) => void;
  queryParam?: string;
  className?: string;
  tablistClassName?: string;
  panelClassName?: string;
};

export function AgTabs({
  children,
  defaultTab,
  aboveTabs,
  onTabChange,
  queryParam,
  className,
  tablistClassName,
  panelClassName,
}: AgTabsProps) {
  const searchStr = useSearchString();
  const navigate = useNavigate();

  const tabs = Children.toArray(children).filter(
    (child): child is ReactElement<AgTabProps> => isValidElement(child) && child.type === AgTab
  );

  const firstTabId = tabs[0]?.props.id;
  const urlKey = (queryParam ? new URLSearchParams(searchStr).get(queryParam) : null) ?? undefined;
  const resolvedKey = urlKey ?? defaultTab ?? firstTabId;

  return (
    <Tabs
      {...(queryParam ? { selectedKey: resolvedKey } : { defaultSelectedKey: resolvedKey })}
      className={cn('grid w-full min-w-0', className)}
      onSelectionChange={(key: Key) => {
        onTabChange?.(key);

        if (queryParam) {
          void navigate({ replace: true, search: (prev) => ({ ...prev, [queryParam]: String(key) }), to: '.' });
        }
      }}
    >
      {aboveTabs}
      <TabList className={cn('flex gap-1 mb-3.5 border-b border-neutral-5', tablistClassName)}>
        {tabs.map((tab) => (
          <Tab
            key={tab.props.id}
            id={tab.props.id}
            data-testid={tab.props['data-testid']}
            className={({ isSelected }) =>
              cn(
                // pointer-events-auto re-enables clicks on the tab itself when a
                // consumer sets pointer-events-none on the TabList (e.g. a tablist
                // pulled over a toolbar via negative margin) so the empty tablist
                // region passes clicks through while the tabs stay interactive.
                'pointer-events-auto relative flex cursor-pointer items-center gap-4 px-3.5 py-2.5 text-14 font-medium outline-none transition-colors',
                'text-neutral-alpha-8 hover:text-neutral-11',
                isSelected && 'text-brand-text'
              )
            }
          >
            <span className="relative flex items-center gap-1">
              {tab.props.indicatorValue && <TabIndicator value={tab.props.indicatorValue} />}
              {tab.props.icon} {tab.props.label} {tab.props.iconAfter}
            </span>
            {/* 2px indicator pushed 1px past the tab edge so it overlaps the
                TabList's 1px bottom divider — matches the design's
                marginBottom:-1 trick. */}
            <SelectionIndicator className="absolute -bottom-px left-0 h-[2px] w-full rounded-sm bg-brand-text [transition-property:translate,width] duration-200 motion-reduce:transition-none" />
          </Tab>
        ))}
      </TabList>
      {tabs.map((tab) => (
        <TabPanel key={tab.props.id} id={tab.props.id} className={cn('min-w-0', panelClassName)}>
          {tab.props.children}
        </TabPanel>
      ))}
    </Tabs>
  );
}

/**
 * Declarative data carrier for AgTabs — never renders anything itself.
 * AgTabs extracts props (id, label, icon, children) from each AgTab to build
 * react-aria's Tab (header) and TabPanel (content) in separate DOM locations.
 */
export function AgTab(_props: AgTabProps) {
  return null;
}

function TabIndicator({ value }: { value: ReactNode }) {
  return (
    <span className="absolute -right-2 -top-1 flex h-[15px] w-[15px] items-center justify-center rounded-full bg-blue-text text-[11px] leading-none text-white shadow-sm animate-pulse">
      {value}
    </span>
  );
}
